import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Quote } from "lucide-react";
import { useFetch } from "@/lib/content";
import { useSEO, articleSchema } from "@/lib/seo";
import { Eyebrow, Reveal, ParallaxImage, CtaButton } from "@/components/site/Motion";
import CaseStudyNav from "@/components/site/CaseStudyNav";
import RichText from "@/components/site/RichText";

const SECTIONS = [
  { key: "context", label: "Context" },
  { key: "problem", label: "Problem" },
  { key: "approach", label: "Approach" },
  { key: "outcome", label: "Outcome" },
];

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { data: c, loading, error } = useFetch(`/case-studies/${slug}`);

  useSEO({
    title: c?.seo_title || (c ? `${c.title} — Case Study | RenB Solutions` : "Case Study | RenB Solutions"),
    description: c?.seo_description || c?.context?.replace(/<[^>]+>/g, "").slice(0, 155),
    type: "article",
    path: `/case-studies/${slug}`,
    image: c?.cover_image,
    jsonLd: c
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: c.title,
            about: c.industry,
            author: { "@type": "Organization", name: "RenB Solutions" },
          },
        ]
      : [],
  });

  if (loading)
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <p className="text-xs uppercase tracking-[0.25em] text-slategray">Loading…</p>
      </div>
    );

  if (error || !c)
    return (
      <div className="mx-auto max-w-[1440px] px-5 pb-24 pt-40 md:px-10">
        <h1 className="font-serif text-5xl">Case study not found.</h1>
        <Link to="/case-studies" className="mt-6 inline-block text-sm font-bold uppercase tracking-[0.14em] text-progress">
          ← All case studies
        </Link>
      </div>
    );

  return (
    <div data-testid="case-study-detail">
      <header className="mx-auto max-w-[1440px] px-5 pb-12 pt-28 md:px-10 md:pb-16 md:pt-40">
        <Link to="/case-studies" data-testid="case-back-link" className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-slategray transition-colors hover:text-progress">
          <ArrowLeft size={14} /> Case Studies
        </Link>
        <Reveal className="mt-8">
          <p className="text-[13px] font-bold uppercase tracking-[0.2em] text-progress">
            {c.client} · {c.industry} · {c.company_size} · {c.series_code} Series
          </p>
          <h1 className="mt-6 max-w-5xl font-serif text-5xl font-medium leading-[1.03] tracking-tight md:text-7xl">
            {c.title}
          </h1>
        </Reveal>
      </header>

      {c.cover_image && (
        <ParallaxImage src={c.cover_image} alt={c.title} imgClassName="grayscale" className="mx-auto aspect-[21/9] max-w-[1440px] border-y border-ash" />
      )}

      <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:px-10 md:py-24 lg:grid-cols-12">
        {/* sticky rail */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <div className="border border-ash">
                <div className="border-b border-ash p-6">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Engagement</p>
                  <dl className="mt-4 space-y-2 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-slategray">Client</dt><dd className="font-semibold text-right">{c.client}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-slategray">Industry</dt><dd className="font-semibold">{c.industry}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-slategray">Company size</dt><dd className="font-semibold">{c.company_size}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-slategray">Series</dt><dd className="font-semibold text-progress">{c.series_code} Series</dd></div>
                  </dl>
                </div>
                {c.metrics?.length > 0 && (
                  <div className="p-6" data-testid="case-metrics">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">Metrics</p>
                    <div className="mt-4 space-y-5">
                      {c.metrics.map((m, i) => (
                        <div key={i}>
                          <p className="font-serif text-5xl font-semibold leading-none text-progress">{m.value}</p>
                          <p className="mt-1 text-[12px] text-slategray">{m.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-10 hidden lg:block">
                <CtaButton testid="case-rail-cta" href="/book" variant="dark" className="w-full justify-center">
                  Book a Discovery Call
                </CtaButton>
              </div>
            </Reveal>
          </div>
        </aside>

        {/* narrative */}
        <article className="lg:col-span-7 lg:col-start-6">
          {SECTIONS.filter((s) => c[s.key]).map((s, i) => (
            <Reveal key={s.key} className="border-t border-ash py-10 first:border-t-0 first:pt-0 md:py-14">
              <p className="font-serif text-2xl font-medium text-progress md:text-3xl">{String(i + 1).padStart(2, "0")}</p>
              <h2 className="mt-3 font-serif text-3xl font-semibold tracking-tight md:text-5xl">{s.label}</h2>
              <RichText html={c[s.key]} className="mt-6 text-obsidian/80" />
              {s.key === "problem" && c.quote_text && (
                <figure className="my-12 border-l-[3px] border-progress pl-6 md:pl-10" data-testid="case-pull-quote">
                  <Quote size={24} className="mb-4 text-progress" aria-hidden="true" />
                  <blockquote className="font-serif text-2xl font-medium italic leading-[1.35] tracking-tight md:text-[2rem]">
                    {c.quote_text}
                  </blockquote>
                  <figcaption className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-slategray">
                    {c.quote_author}
                  </figcaption>
                </figure>
              )}
            </Reveal>
          ))}
        </article>
      </div>

      <CaseStudyNav currentSlug={slug} />

      <section className="border-t border-ash bg-obsidian text-paper">
        <div className="mx-auto max-w-[1440px] px-5 py-20 text-center md:px-10 md:py-28">
          <h2 className="mx-auto max-w-3xl font-serif text-4xl font-medium leading-tight tracking-tight md:text-6xl">
            Facing something similar in your business<span className="text-progress">?</span>
          </h2>
          <div className="mt-10">
            <CtaButton testid="case-bottom-cta" href="/book">
              Book a Discovery Call
            </CtaButton>
          </div>
        </div>
      </section>
    </div>
  );
}
